import { Credentials, Property } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_PROPERTY } from "@/constants/resultsLimits";
import { createGeotabClient } from "@/utilities/geotab/createGeotabClient";

const EMPTY: Record<string, Property> = {};

/**
 * All custom property definitions of the database, keyed by id. Reading them needs the
 * "ViewProperty" clearance, which auditor roles often lack. The shared client batches reads into
 * one ExecuteMultiCall and a forbidden call fails the whole batch, so this call goes through its
 * own unqueued client and a refusal yields an empty map instead of an error.
 */
export function useProperties() {
  const { username, database, internalSessionId, activeSessionId } = useGeotabClient();
  const client = useMemo(
    () =>
      internalSessionId
        ? createGeotabClient({ credentials: { userName: username, database, sessionId: internalSessionId } as Credentials, queueMethods: [] })
        : null,
    [username, database, internalSessionId],
  );

  return useQuery<Record<string, Property>>({
    queryKey: ["properties", activeSessionId],
    enabled: !!client,
    retry: false,
    queryFn: async ({ signal }) => {
      if (!client) return EMPTY;
      try {
        const properties = await client.get("Property", {}, RESULTS_LIMIT_PROPERTY, signal);
        return Object.fromEntries(properties.map(property => [property.id, property]));
      } catch (error) {
        if (error instanceof Error && /not permitted/i.test(error.message)) return EMPTY;
        throw error;
      }
    },
  });
}
